import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  Divider,
  Statistic,
  Alert,
  message
} from 'antd';
import {
  CarOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  ToolOutlined,
  CalendarOutlined,
  ScheduleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text } = Typography;

interface Vehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  vin?: string;
  license_plate?: string;
  color?: string;
  mileage?: number;
  created_at: string;
  estimate_count?: number;
  work_order_count?: number;
  last_service_date?: string;
}

const CustomerVehicles: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, customerData } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadVehicles();
  }, [isAuthenticated]);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await customerPortalApi.get('/customer-portal/vehicles');
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      message.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const customer = customerData?.customer;

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Row align="middle" justify="space-between">
              <Col>
                <Space direction="vertical" size={0}>
                  <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/customer/dashboard')}
                    style={{ padding: 0, marginBottom: 8 }}
                  >
                    Back to Dashboard
                  </Button>
                  <Title level={3} style={{ margin: 0 }}>
                    <CarOutlined /> My Vehicles
                  </Title>
                  <Text type="secondary">
                    {customer?.first_name} {customer?.last_name}'s vehicles
                  </Text>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/book')}
                >
                  Add Vehicle via Service
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Vehicle Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Vehicles"
                  value={vehicles.length}
                  prefix={<CarOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Total Services"
                  value={vehicles.reduce((sum, v) => sum + ((v.estimate_count || 0) + (v.work_order_count || 0)), 0)}
                  prefix={<ToolOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Most Recent Service"
                  value={
                    vehicles.length > 0 && vehicles.some(v => v.last_service_date)
                      ? dayjs(
                          vehicles
                            .filter(v => v.last_service_date)
                            .sort((a, b) =>
                              dayjs(b.last_service_date).diff(dayjs(a.last_service_date))
                            )[0]?.last_service_date
                        ).format('MMM D, YYYY')
                      : 'N/A'
                  }
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#fa8c16', fontSize: 16 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Vehicles List */}
          <Card title="Your Vehicles">
            {vehicles.length > 0 ? (
              <List
                grid={{
                  gutter: 16,
                  xs: 1,
                  sm: 1,
                  md: 2,
                  lg: 2,
                  xl: 3,
                  xxl: 3,
                }}
                dataSource={vehicles}
                renderItem={(vehicle: Vehicle) => (
                  <List.Item>
                    <Card
                      hoverable
                      style={{
                        borderRadius: 12,
                        height: '100%',
                      }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        {/* Vehicle Icon and Title */}
                        <div style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          padding: '20px',
                          borderRadius: 8,
                          textAlign: 'center'
                        }}>
                          <CarOutlined style={{ fontSize: 48, color: 'white' }} />
                        </div>

                        {/* Vehicle Info */}
                        <div>
                          <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </Title>

                          {vehicle.color && (
                            <Tag color="blue">{vehicle.color}</Tag>
                          )}
                          {vehicle.license_plate && (
                            <Tag color="purple">{vehicle.license_plate}</Tag>
                          )}
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        {/* Details */}
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          {vehicle.vin && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>VIN</Text>
                              <br />
                              <Text strong style={{ fontSize: 12 }}>{vehicle.vin}</Text>
                            </div>
                          )}

                          {typeof vehicle.mileage === 'number' && vehicle.mileage > 0 && (
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>Current Mileage</Text>
                              <br />
                              <Text strong>{vehicle.mileage.toLocaleString()} miles</Text>
                            </div>
                          )}

                          <Divider style={{ margin: '8px 0' }} />

                          {/* Service Stats */}
                          <Row gutter={8}>
                            <Col span={12}>
                              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f2f5', borderRadius: 6 }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>Services</Text>
                                <br />
                                <Text strong style={{ fontSize: 16 }}>
                                  {(vehicle.estimate_count || 0) + (vehicle.work_order_count || 0)}
                                </Text>
                              </div>
                            </Col>
                            <Col span={12}>
                              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f2f5', borderRadius: 6 }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>Last Service</Text>
                                <br />
                                <Text strong style={{ fontSize: 12 }}>
                                  {vehicle.last_service_date
                                    ? dayjs(vehicle.last_service_date).format('MMM YYYY')
                                    : 'Never'}
                                </Text>
                              </div>
                            </Col>
                          </Row>
                        </Space>

                        <Divider style={{ margin: '8px 0' }} />

                        {/* Actions */}
                        <Button
                          block
                          type="primary"
                          icon={<CalendarOutlined />}
                          onClick={() => navigate('/book')}
                        >
                          Book Service
                        </Button>
                        {/* WP-13 slice 4 — this vehicle's maintenance schedule. */}
                        <Button
                          block
                          icon={<ScheduleOutlined />}
                          onClick={() => navigate(`/customer/vehicles/${vehicle.id}/service-table`)}
                        >
                          Service Schedule
                        </Button>
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={
                  <Space direction="vertical" size="middle">
                    <Text>No vehicles found</Text>
                    <Text type="secondary">
                      Vehicles will be automatically added when you book a service or we perform work on your vehicle.
                    </Text>
                  </Space>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '60px 20px' }}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<CalendarOutlined />}
                  onClick={() => navigate('/book')}
                >
                  Book Your First Service
                </Button>
              </Empty>
            )}
          </Card>

          {/* Info Card */}
          <Alert
            style={{ marginTop: 24 }}
            message="About Your Vehicles"
            description="Your vehicles are tracked from service appointments and work orders. Book a service to automatically add a vehicle to your account."
            type="info"
            showIcon
          />
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerVehicles;
